import { fetcher } from '../../../lib/api-fetcher';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';


export const rbacKeys = {
  all: ['rbac'] as const,
  roles: () => [...rbacKeys.all, 'roles'] as const,
};

export interface Role {
  id: string;
  name: string;
  type: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RolesMeta {
  total: number;
  page: number;
  limit: number;
}

export function useRolesPaginated(page: number, limit: number) {
  return useQuery({
    queryKey: [...rbacKeys.roles(), page, limit],
    queryFn: async () => {
      return fetcher.get<{ data: Role[]; meta: RolesMeta }>(`/staff/v1/rbac/roles?page=${page}&limit=${limit}`);
    },
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fetcher.delete(`/staff/v1/rbac/roles/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rbacKeys.roles() });
    },
  });
}

export interface Permission {
  id: string;
  code: string;
  description: string | null;
}

export function usePermissions(page = 1, limit = 500) {
  return useQuery({
    queryKey: [...rbacKeys.all, 'permissions', page, limit],
    queryFn: async () => {
      return fetcher.get<{ data: Permission[]; meta: RolesMeta }>(`/staff/v1/rbac/permissions?page=${page}&limit=${limit}`);
    },
  });
}

export function useRolePermissions(roleId: string | null | undefined) {
  return useQuery({
    queryKey: [...rbacKeys.all, 'role-permissions', roleId],
    queryFn: async () => {
      if (!roleId) return { data: [] };
      return fetcher.get<{ data: string[] }>(`/staff/v1/rbac/roles/${roleId}/permissions`);
    },
    enabled: !!roleId,
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string }) =>
      fetcher.post<Role>(`/staff/v1/rbac/roles`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rbacKeys.roles() });
    },
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { name: string; description?: string } }) =>
      fetcher.put<Role>(`/staff/v1/rbac/roles/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rbacKeys.roles() });
    },
  });
}

export function useSetRolePermissions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, permissionIds }: { id: string; permissionIds: string[] }) =>
      fetcher.post<void>(`/staff/v1/rbac/roles/${id}/permissions`, { permissionIds }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [...rbacKeys.all, 'role-permissions', id] });
    },
  });
}
